// react
import { forwardRef, useEffect, useRef, useState } from 'react'

// external libs
import { Box, Dialog, DialogContent, DialogTitle, IconButton, makeStyles, Slide, Typography } from '@material-ui/core'
import { TransitionProps } from '@material-ui/core/transitions'
import { Close, QuestionAnswerRounded } from '@material-ui/icons'

// components
import MessengerItem from './MessengerItem'
import MessengerInput from './MessengerInput'

// hooks
import useCheckPermission from '../../../../utilities/checkPermission'
import useTranslate from '../../../../i18n/useTranslate'
import useMessenger from '../../../../services/web/useMessenger'

// types
import type { Messenger as MessengerType, SenderRole } from '../../../../services/web/useMessenger'
import type { GetProjectResultType as Project } from '../../../../services/web/types/responseWebServiceTypes'

interface MessengerProps {
    project: Project
}

const Messenger = (props: MessengerProps) => {
    const { project } = props

    const classes = useStyles()
    const messengerServices = useMessenger()
    const checkPermission = useCheckPermission()
    const senderRole: SenderRole = checkPermission.isUserResearcher() ? 'researcher' : 'officer'
    const translate = useTranslate()
    const currentLanguage = translate.languageStore.currentLanguage

    const messagesEndRef = useRef<HTMLDivElement>(null)

    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [messenger, setMessenger] = useState<MessengerType[]>([])
    const [hasUnreadMessage, setHasUnreadMessage] = useState<boolean>(false)

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' })
    }

    const handleMessageSent = (value: MessengerType) => {
        setMessenger((prev) => [...prev, value])
        setTimeout(scrollToBottom, 100)
    }

    useEffect(() => {
        if (isOpen) {
            const fetchMessenger = async () => {
                try {
                    setIsLoading(true)
                    const { result } = await messengerServices.getMessengers(project.projectID, { senderRole })
                    setMessenger(result ?? [])
                    setTimeout(scrollToBottom, 100)
                    setHasUnreadMessage(false)
                } catch (error) {
                    console.error(error)
                } finally {
                    setIsLoading(false)
                }
            }

            fetchMessenger()
        }
    }, [isOpen])

    useEffect(() => {
        const fetchUnreadCount = async () => {
            try {
                const { result } = await messengerServices.getMessengersUnreadCount(project.projectID, { senderRole })
                setHasUnreadMessage(result.unreadCount > 0)
            } catch (error) {
                console.error(error)
            }
        }

        fetchUnreadCount()
    }, [])

    return (
        <>
            {!isOpen ? (
                <Box className={classes.buttonWrapper}>
                    <Box position="relative">
                        <IconButton color="primary" className={classes.button} onClick={() => setIsOpen(true)}>
                            <QuestionAnswerRounded />
                        </IconButton>
                    </Box>
                    {hasUnreadMessage ? <Box className={classes.buttonBadge} /> : null}
                </Box>
            ) : null}

            <Dialog
                open={isOpen}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="md"
                keepMounted
                scroll="paper"
                className={classes.dialog}
                onClose={() => setIsOpen(false)}
            >
                <DialogTitle className={classes.dialogTitle}>
                    <Box display="flex" justifyContent="space-between">
                        <Box flexGrow={1}>
                            <Typography variant="h2" className={classes.title}>
                                {translate.get('UM.MESSAGE.PROJECT_NO', [project.projectCode ?? '-'])}
                            </Typography>
                            <Typography>
                                {`${translate.get('UM.MESSAGE.PROJECT_NO', [project.projectCode ?? '-'])}: ${
                                    currentLanguage === 'th' ? project.projectName_th : project.projectName_en
                                }`}
                            </Typography>
                        </Box>
                        <Box flexShrink={0}>
                            <IconButton onClick={() => setIsOpen(false)}>
                                <Close />
                            </IconButton>
                        </Box>
                    </Box>
                </DialogTitle>

                <DialogContent className={classes.dialogContent}>
                    <Box className={classes.messagesWrapper}>
                        {isLoading ? <Box>{translate.get('UM.MESSAGE.LOADING')}</Box> : null}

                        {messenger.map((item, index) => (
                            <MessengerItem key={index} messenger={item} />
                        ))}
                        <div ref={messagesEndRef} />
                    </Box>

                    <MessengerInput projectId={project.projectID} onMessageSent={handleMessageSent} />
                </DialogContent>
            </Dialog>
        </>
    )
}

const Transition = forwardRef(function Transition(
    props: TransitionProps & { children?: React.ReactElement<any, any> },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />
})

const useStyles = makeStyles((theme) => ({
    buttonWrapper: {
        right: 45,
        bottom: 200,
        position: 'fixed',
        zIndex: 10,
    },

    button: {
        width: 52,
        height: 52,
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        boxShadow: theme.shadows[4],
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            boxShadow: theme.shadows[20],
        },
    },

    buttonBadge: {
        position: 'absolute',
        borderRadius: '100%',
        width: 16,
        height: 16,
        backgroundColor: theme.palette.secondary.main,
        flexShrink: 0,
        shadow: theme.shadows[14],
        border: '3px solid #fff',
        top: 2,
        right: 2,
    },

    dialog: {
        '& .MuiPaper-root': {
            borderRadius: 8,
        },
    },

    dialogTitle: {
        color: theme.palette.common.white,
        backgroundColor: theme.palette.primary.main,
    },

    title: {
        fontSize: 24,
        fontWeight: 700,
    },

    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        height: 600,
        padding: '16px 0 16px 0',
        overflow: 'hidden',
    },

    messagesWrapper: {
        flex: 1,
        paddingInline: 32,
        overflowY: 'auto',
        marginBottom: 8,
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
    },
}))

export default Messenger
